import { logger } from '../utils/logger';
import { fetchWithAppCheck } from '../utils/fetchWithAppCheck';

const FIREBASE_FUNCTIONS_URL = 'https://us-central1-aigicare-prod.cloudfunctions.net';

const resolveAdminEmailFromStorage = () => {
  try {
    const adminProfile = JSON.parse(localStorage.getItem('UserProfileAdmin') || '{}');
    if (adminProfile?.email) {
      logger.info('[ZoomService] Admin email resolved from UserProfileAdmin:', adminProfile.email);
      return adminProfile.email;
    }
  } catch (e) {
    // ignore parse errors
  }

  try {
    const userProfile = JSON.parse(localStorage.getItem('UserProfile') || '{}');
    if (userProfile?.email) {
      logger.info('[ZoomService] Admin email resolved from UserProfile:', userProfile.email);
      return userProfile.email;
    }
  } catch (e) {
    // ignore parse errors
  }

  const fallbacks = ['AdminEmail', 'adminEmail', 'UserEmailAdmin', 'userEmail', 'email'];
  for (const key of fallbacks) {
    const value = localStorage.getItem(key);
    if (value) {
      logger.info(`[ZoomService] Admin email resolved from localStorage key "${key}":`, value);
      return value;
    }
  }

  logger.warn('[ZoomService] ⚠️ Admin email could NOT be resolved from localStorage. Admin will not receive meeting invite.');
  return '';
};

const postJson = async (url, body) => {
  const response = await fetchWithAppCheck(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok || data.status === 'error') {
    throw new Error(data.message || data.details || 'Request failed');
  }

  return data;
};

export const createZoomMeeting = async (meetingData) => {
  const adminEmail = meetingData?.admin_email || resolveAdminEmailFromStorage();
  const payload = {
    ...meetingData,
    admin_email: adminEmail,
    timezone: meetingData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  logger.info('[ZoomService] ▶ createZoomMeeting called');
  logger.info('[ZoomService] Patient (attendee) email:', payload.attendee || '⚠️ MISSING');
  logger.info('[ZoomService] Admin email:', adminEmail || '⚠️ MISSING — admin will NOT get invite');
  logger.info('[ZoomService] Session ID:', payload.session_id || '⚠️ MISSING');
  logger.info('[ZoomService] Topic:', payload.topic || '⚠️ MISSING');
  logger.info('[ZoomService] Start time:', payload.start_time || '⚠️ MISSING');
  logger.info('[ZoomService] Full payload sent to Cloud Function:', payload);

  try {
    const result = await postJson(`${FIREBASE_FUNCTIONS_URL}/createZoomMeeting`, payload);
    logger.info('[ZoomService] ✅ createZoomMeeting Cloud Function response:', result);
    logger.info('[ZoomService] Email invite status from response:', result?.email_results || result?.invite_results || '(not returned in response)');
    return result;
  } catch (error) {
    logger.error('[ZoomService] ❌ createZoomMeeting failed:', error.message);
    throw error;
  }
};

export const createFollowupZoomMeeting = async (meetingData) => {
  const adminEmail = meetingData?.admin_email || resolveAdminEmailFromStorage();
  const payload = {
    ...meetingData,
    admin_email: adminEmail,
    timezone: meetingData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  logger.info('[ZoomService] ▶ createFollowupZoomMeeting called');
  logger.info('[ZoomService] Patient (attendee) email:', payload.attendee || '⚠️ MISSING');
  logger.info('[ZoomService] Admin email:', adminEmail || '⚠️ MISSING — admin will NOT get invite');
  logger.info('[ZoomService] Session ID:', payload.session_id || '⚠️ MISSING');
  logger.info('[ZoomService] Full payload:', payload);

  try {
    const result = await postJson(`${FIREBASE_FUNCTIONS_URL}/createFollowupZoomMeeting`, payload);
    logger.info('[ZoomService] ✅ createFollowupZoomMeeting response:', result);
    return result;
  } catch (error) {
    logger.error('[ZoomService] ❌ createFollowupZoomMeeting failed:', error.message);
    throw error;
  }
};

export const processZoomMeeting = async (meetingData) => {
  return await postJson(`${FIREBASE_FUNCTIONS_URL}/processZoomMeeting`, meetingData);
};

export const deleteZoomMeeting = async (meetingId) => {
  const response = await fetchWithAppCheck(`${FIREBASE_FUNCTIONS_URL}/deleteZoomMeeting?meeting_id=${encodeURIComponent(meetingId)}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok || data.status === 'error') {
    throw new Error(data.message || data.details || 'Failed to delete Zoom meeting');
  }
  return data;
};
